import { ExpandMore } from '@material-ui/icons'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material'
import { ReactNode, useMemo, useState } from 'react'
import { StyledAccordionExpandIcon } from 'shared/components/Accordion/Accordion'
import { FlexBox } from 'shared/components/Base/Base'
import { Text, TypographyVariant } from 'shared/components/Text/Text'
import { SafetyEvent, SafetyEventCategory } from 'shared/models/compliance'
import { lightGray1, white } from 'shared/theme/colors'
import styled from 'styled-components'

import { SafetyEventAccordion } from '../SafetyEventFilters/SafetyEventAccordion'

// Part of this table is adapted from:
// https://mui.com/material-ui/react-table/#system-EnhancedTable.tsx
type Order = 'asc' | 'desc'

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (!a[orderBy] || !b[orderBy]) {
    return 0
  }
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | null | boolean },
  b: { [key in Key]: number | string | null | boolean }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

interface Column {
  headerName: string
  field: keyof SafetyEvent
  formatter?: (e: SafetyEvent) => ReactNode
  width?: number
  numeric?: boolean
  fontVariant?: TypographyVariant
  fontColor?: string
}

interface SafetyToolEventsListProps {
  rows: SafetyEvent[]
  columns: Column[]
  defaultOrderBy?: keyof SafetyEvent
}

function SortableHeader({
  order,
  orderBy,
  onRequestSort,
  columns,
}: {
  columns: Column[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof SafetyEvent) => void
}) {
  const createSortHandler = (property: keyof SafetyEvent) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <StyledHeaderRow>
        {columns.map((column) => (
          <StyledHeaderCell
            key={column.field}
            align={column.numeric ? 'right' : 'left'}
            padding="normal"
            sortDirection={orderBy === column.field ? order : false}
            $width={column.width}
          >
            <TableSortLabel
              active={orderBy === column.field}
              direction={orderBy === column.field ? order : 'asc'}
              onClick={createSortHandler(column.field)}
            >
              <Text variant="header4" fontColor={lightGray1}>
                {column.headerName}
              </Text>
            </TableSortLabel>
          </StyledHeaderCell>
        ))}
        <TableCell>{/*{ Empty cell for accordion icon }*/}</TableCell>
      </StyledHeaderRow>
    </TableHead>
  )
}

function ToggleRow({ row, columns }: { row: SafetyEvent; columns: Column[] }) {
  const [isExpanded, setIsExpanded] = useState(false)
  // Only allow toggle for accidents and violations
  const showToggle = row.eventCategory === SafetyEventCategory.ACCIDENTS_AND_VIOLATIONS
  return (
    <>
      <StyledRow onClick={showToggle ? () => setIsExpanded((x) => !x) : undefined}>
        {columns.map((column) => (
          <StyledTableCell key={column.field} align={column.numeric ? 'right' : 'left'} $width={column.width}>
            <Text variant={column.fontVariant ?? 'header3'} fontColor={column.fontColor}>
              {column.formatter?.(row) || row[column.field]}
            </Text>
          </StyledTableCell>
        ))}
        <TableCell>
          <FlexBox justifyContent="center" alignItems="center">
            {showToggle && (
              <StyledAccordionExpandIcon $isExpanded={isExpanded}>
                <ExpandMore />
              </StyledAccordionExpandIcon>
            )}
          </FlexBox>
        </TableCell>
      </StyledRow>
      <tr>
        <td colSpan={columns.length + 1}>
          <SafetyEventAccordion safetyEvent={row} isExpanded={isExpanded} />
        </td>
      </tr>
    </>
  )
}

export function SafetyToolEventsList({ rows, columns, defaultOrderBy }: SafetyToolEventsListProps) {
  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<keyof SafetyEvent>(defaultOrderBy ?? columns[0].field)
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof SafetyEvent) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedRows = useMemo(() => stableSort(rows, getComparator(order, orderBy)), [rows, order, orderBy])

  return (
    <Container>
      <Table>
        <SortableHeader columns={columns} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
        <TableBody>
          {sortedRows.map((row) => (
            <ToggleRow key={row.eventId} row={row} columns={columns} />
          ))}
        </TableBody>
      </Table>
    </Container>
  )
}

const Container = styled(TableContainer)`
  background-color: ${white};
  border-radius: 3px;
`

const StyledRow = styled(TableRow)`
  td {
    padding: 24px 8px !important;
  }

  td:nth-child(1) {
    padding: 16px 8px 16px 24px !important;
  }

  td:nth-last-child(1) {
    padding: 16px 24px 16px 8px !important;
  }
`

const StyledHeaderRow = styled(StyledRow)`
  th {
    padding: 16px 8px !important;
  }

  th:nth-child(1) {
    padding: 16px 8px 16px 24px !important;
  }

  th:nth-last-child(1) {
    padding: 16px 24px 16px 8px !important;
  }
`

const StyledTableCell = styled(TableCell)<{ $width?: number }>`
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
`

const StyledHeaderCell = styled(StyledTableCell)`
  svg {
    color: ${lightGray1} !important;
  }
`
